import { useState, useEffect, useMemo, useCallback, useContext } from "react"
import buildTop10ACollection from "../Dashboarding.functions/buildTop10ACollection"
import DatabaseContext from "../../../contexts/Database/DatabaseContext"

const useTop10APage = (filters = {}) => {
  const [data, setData] = useState({
    topCountries: [],
    topClients: [],
    topProducts: [],
    topProductsBySales: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [years, setYears] = useState({ THIS_YEAR: null, LAST_YEAR: null })
  const [metadata, setMetadata] = useState(null)

  const { dashboardSalesOrders, isLoading: dbLoading } = useContext(DatabaseContext)

  // Mémoisation des données brutes pour éviter les rechargements inutiles
  const rawData = useMemo(() => {
    return dashboardSalesOrders || []
  }, [dashboardSalesOrders])

  // Mémoisation des filtres pour éviter les recalculs inutiles
  const memoizedFilters = useMemo(() => {
    const {
      entity = "WGE",
      currency = "C€",
      period = "FY",
      products = [],
      countries = [],
      clients = [],
      limit = 10,
    } = filters

    return {
      entity,
      currency,
      period,
      products: Array.isArray(products) ? products : [],
      countries: Array.isArray(countries) ? countries : [],
      clients: Array.isArray(clients) ? clients : [],
      limit: Number(limit),
    }
  }, [
    filters.entity,
    filters.currency,
    filters.period,
    filters.limit,
    JSON.stringify(filters.products),
    JSON.stringify(filters.countries),
    JSON.stringify(filters.clients),
  ])

  // Fonction de chargement des données avec gestion d'erreurs robuste
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Validation des données brutes
      if (!rawData || rawData.length === 0) {
        throw new Error('No raw data available')
      }

      // Déterminer les entités disponibles en se basant uniquement sur order_type_sales
      const availableEntities = ["WGALL"]
      const hasWGE = rawData.some(row => row.order_type_sales === "WGE")
      const hasWGUK = rawData.some(row => row.order_type_sales === "WGUK")

      if (hasWGE) availableEntities.push("WGE")
      if (hasWGUK) availableEntities.push("WGUK")

      // Transformation des données
      const result = buildTop10ACollection(rawData, memoizedFilters)

      // Mise à jour de l'état
      setData({
        topCountries: result.topCountries || [],
        topClients: result.topClients || [],
        topProducts: result.topProducts || [],
        topProductsBySales: result.topProductsBySales || [],
      })

      setYears({
        THIS_YEAR: result.THIS_YEAR,
        LAST_YEAR: result.LAST_YEAR,
      })

      setMetadata({
        ...result.metadata,
        availableEntities,
      })
    } catch (err) {
      console.error('❌ Error loading Top10A data:', err)
      setError(err.message || 'An error occurred while loading data')

      // État de fallback
      setData({
        topCountries: [],
        topClients: [],
        topProducts: [],
        topProductsBySales: [],
      })
      setYears({ THIS_YEAR: null, LAST_YEAR: null })
      setMetadata(null)
    } finally {
      setIsLoading(false)
    }
  }, [rawData, memoizedFilters])

  // Effet pour charger les données
  useEffect(() => {
    if (!dbLoading) {
      if (rawData?.length > 0) {
        loadData()
      } else {
        // No data available, but not loading anymore
        setIsLoading(false)
        setData({
          topCountries: [],
          topClients: [],
          topProducts: [],
          topProductsBySales: [],
        })
        setYears({ THIS_YEAR: null, LAST_YEAR: null })
        setMetadata(null)
        setError(null)
      }
    }
  }, [dbLoading, rawData, loadData])

  return {
    ...data,
    THIS_YEAR: years.THIS_YEAR,
    LAST_YEAR: years.LAST_YEAR,
    isLoading: isLoading || dbLoading,
    error,
    metadata,
  }
}

export default useTop10APage
